import IconCopy from "assets/img/icon-copy.svg?react"

import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import Button from "components/Button/Button"
import { Flex } from "components/Flex"
import { IApiLink } from "@/types"
import toastMessages from "stores/toastMessages"
import Tooltip from "components/Tooltip/Tooltip"

interface IFeaturesProps {
    features: string | undefined
    linkGetAssetCollection?: IApiLink
    onCopyClick?(): Promise<void>
}

const Features: FC<IFeaturesProps> = ({
    features,
    onCopyClick,
}) => {
    const { t } = useTranslation()
    const textRef = useRef<HTMLSpanElement>(null)
    const [expanded, setExpanded] = useState<boolean | null>(null)
    const [isCopying, setIsCopying] = useState(false)

    async function handleCopyClick() {
        if (!onCopyClick) return
        setIsCopying(true)
        try {
            await onCopyClick()
        } catch (e) {
            toastMessages.showSystemError()
        } finally {
            setIsCopying(false)
        }
    }

    // detect that text is clamped
    const checkExpanded = useCallback(() => {
        if (!textRef.current) return
        const elem = textRef.current
        setExpanded(elem.scrollHeight > elem.clientHeight ? false : null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [features])

    useLayoutEffect(() => {
        checkExpanded()
    }, [checkExpanded])

    useEffect(() => {
        window.addEventListener("resize", checkExpanded)
        return () => window.removeEventListener("resize", checkExpanded)
    }, [checkExpanded])

    const copyButton = onCopyClick
        ? <Tooltip content={t('asset.copyFeaturesTooltip')} trigger="hover">
            <Button
                theme="link"
                label={t('common.copyToClipboard')}
                icon={<IconCopy />}
                onClick={handleCopyClick}
                disabled={isCopying}
                loading={isCopying}
            />
        </Tooltip>
        : null

    if (!features) return copyButton

    return (
        <>
            <FlexibleContent
                text={features}
                clamp={expanded ? -1 : 8}
                clampedTooltip={false}
                className="white-space-pre-wrap"
                ref={textRef}
            />
            {(typeof expanded === 'boolean' || !!copyButton) &&
                <Flex className="mt-2" gap="2.4rem">
                    {typeof expanded === 'boolean' &&
                        <Button
                            theme="link"
                            label={expanded ? t('common.showLess') : t('common.showMore')}
                            onClick={() => setExpanded(current => !current)}
                        />
                    }
                    {copyButton}
                </Flex>
            }
        </>
    )
}

export default Features
